import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getMetaPixelPublic } from "../lib/api";
import { trackPageView } from "../lib/metaPixel";

/**
 * MetaPixelLoader — iter47t. Injects Meta's official fbq() snippet
 * once per page load, then fires `PageView` on every SPA route change.
 *
 * Behavior mirrors TidioLoader.jsx:
 *   1. On mount, fetch the saved Pixel ID from GET /api/site/meta-pixel.
 *   2. Inject the standard `<script>` that defines window.fbq.
 *   3. Init the Pixel via `fbq('init', PIXEL_ID)` and fire the first
 *      PageView.
 *   4. On every subsequent React Router navigation, fire another
 *      PageView (SPAs don't trigger Meta's auto-tracker on route change).
 *
 * Idempotent — the script element carries a `data-meta-pixel-injected`
 * attribute, so a second mount (HMR / tab switch) is a no-op.
 */
const SCRIPT_ID = "meta-pixel-script";

export default function MetaPixelLoader() {
  const location = useLocation();

  // One-time injection
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getMetaPixelPublic();
        if (cancelled || !cfg?.pixel_id) return;
        if (document.getElementById(SCRIPT_ID)) return;

        // Standard Meta Pixel base code (transcribed from the official
        // Events Manager install instructions). We DON'T eval a raw
        // string from the server — the only dynamic piece is the
        // numeric pixel_id which we've already validated server-side.
        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.setAttribute("data-meta-pixel-injected", "1");
        s.text = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${cfg.pixel_id}');
        `;
        document.head.appendChild(s);

        // <noscript> fallback pixel — same as Meta's official snippet.
        const ns = document.createElement("noscript");
        const img = document.createElement("img");
        img.height = 1;
        img.width = 1;
        img.style.display = "none";
        img.src = `https://www.facebook.com/tr?id=${cfg.pixel_id}&ev=PageView&noscript=1`;
        ns.appendChild(img);
        document.head.appendChild(ns);
      } catch {
        // Quiet — no Pixel configured yet means no tracking, which is fine.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SPA route change (and first load) → fire PageView WITH an event_id so
  // the browser Pixel + server CAPI dedupe. Runs on mount too, so the base
  // code no longer fires its own (un-deduped) PageView.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    trackPageView();
  }, [location.pathname]);

  return null;
}
